import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Card, Modal, Space, Table, Tabs, Tag, Typography, message } from 'antd';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import pmvService, {
  type PmvClassification,
  type PmvDocumentRequirement,
  type PmvInspectionTemplate,
  type PmvInternalHireRate,
} from '@/services/pmv.service';

const { Title, Paragraph, Text } = Typography;

const PmvSetupPage: React.FC = () => {
  const navigate = useNavigate();
  const qc = useQueryClient();

  const classQ = useQuery({
    queryKey: ['pmv-classifications'],
    queryFn: () => pmvService.listClassifications(),
  });
  const tplQ = useQuery({
    queryKey: ['pmv-inspection-templates-all'],
    queryFn: () => pmvService.listInspectionTemplates(),
  });
  const reqQ = useQuery({
    queryKey: ['pmv-document-requirements'],
    queryFn: () => pmvService.listDocumentRequirements(),
  });
  const ratesQ = useQuery({
    queryKey: ['pmv-internal-hire-rates'],
    queryFn: () => pmvService.listInternalHireRates(),
  });

  const deactivateMut = useMutation({
    mutationFn: (rateId: string) => pmvService.patchInternalHireRate(rateId, { is_active: false }),
    onSuccess: () => {
      message.success('Hire rate deactivated');
      void qc.invalidateQueries({ queryKey: ['pmv-internal-hire-rates'] });
    },
    onError: (e: Error) => message.error(e.message || 'Deactivate failed'),
  });

  const classifications = classQ.data ?? [];
  const className = (id: string | null | undefined) => {
    const c = classifications.find((x) => x.id === id);
    if (!c) return id ? '—' : 'All classes';
    return c.name;
  };

  return (
    <WorkspaceLayout
      header={
        <EnterprisePageHeader
          documentLabel="Configuration"
          documentNumber=""
          statusTag={<Tag color="purple">Plant &amp; Fleet</Tag>}
        />
      }
    >
      <Tabs
        items={[
          {
            key: 'classifications',
            label: 'Equipment classes',
            children: (
              <>
                <Space style={{ marginBottom: 12 }} wrap>
                  <Title level={5} style={{ margin: 0 }}>
                    Equipment classes
                  </Title>
                  <Button type="primary" onClick={() => navigate('/asset/pmv/setup/classifications/new')}>
                    New class
                  </Button>
                </Space>
                <Card>
                  <Table<PmvClassification>
                    rowKey="id"
                    loading={classQ.isLoading}
                    dataSource={classifications}
                    pagination={false}
                    columns={[
                      {
                        title: 'Name',
                        dataIndex: 'name',
                        render: (name: string, row) => (
                          <Space direction="vertical" size={0}>
                            <Text strong>{name}</Text>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              {row.code}
                              {row.is_system ? ' · system' : ''}
                            </Text>
                          </Space>
                        ),
                      },
                      { title: 'Family', dataIndex: 'class_family' },
                      {
                        title: 'Active',
                        dataIndex: 'is_active',
                        width: 90,
                        render: (v: boolean | undefined) =>
                          v === false ? <Tag>Inactive</Tag> : <Tag color="success">Active</Tag>,
                      },
                      {
                        title: 'Actions',
                        width: 100,
                        render: (_value, row) => (
                          <Button
                            type="link"
                            size="small"
                            onClick={() => navigate(`/asset/pmv/setup/classifications/${row.id}`)}
                          >
                            Edit
                          </Button>
                        ),
                      },
                    ]}
                  />
                </Card>
                <Paragraph type="secondary" style={{ marginTop: 16 }}>
                  Starter classifications seed on first access. Add company-specific classes here; link technical
                  attributes from Equipment Passport specifications after activation.
                </Paragraph>
              </>
            ),
          },
          {
            key: 'inspections',
            label: 'Inspection checklists',
            children: (
              <>
                <Button
                  type="primary"
                  onClick={() => navigate('/asset/pmv/setup/inspection-templates/new')}
                  style={{ marginBottom: 12 }}
                >
                  New template
                </Button>
                <Table<PmvInspectionTemplate>
                  rowKey="id"
                  loading={tplQ.isLoading}
                  dataSource={tplQ.data ?? []}
                  pagination={false}
                  columns={[
                    {
                      title: 'Template',
                      dataIndex: 'template_name',
                      render: (name: string, row) => (
                        <Space direction="vertical" size={0}>
                          <Text strong>{name}</Text>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {row.template_code}
                          </Text>
                        </Space>
                      ),
                    },
                    {
                      title: 'Kind',
                      dataIndex: 'inspection_kind',
                      render: (v: string) => <Tag>{String(v).replace(/_/g, ' ')}</Tag>,
                    },
                    {
                      title: 'Classification',
                      dataIndex: 'classification_id',
                      render: (id: string | null) => className(id),
                    },
                    {
                      title: 'Checklist items',
                      dataIndex: 'checklist_items_json',
                      render: (items: PmvInspectionTemplate['checklist_items_json']) =>
                        Array.isArray(items) ? items.length : 0,
                    },
                    {
                      title: 'Frequency (days)',
                      dataIndex: 'frequency_days',
                      render: (v) => v ?? '—',
                    },
                  ]}
                />
              </>
            ),
          },
          {
            key: 'doc-reqs',
            label: 'Document requirements',
            children: (
              <>
                <Button
                  type="primary"
                  onClick={() => navigate('/asset/pmv/setup/document-requirements/new')}
                  style={{ marginBottom: 12 }}
                >
                  New requirement
                </Button>
                <Table<PmvDocumentRequirement>
                  rowKey="id"
                  loading={reqQ.isLoading}
                  dataSource={reqQ.data ?? []}
                  pagination={false}
                  columns={[
                    {
                      title: 'Requirement',
                      dataIndex: 'label',
                      render: (label: string, row) => (
                        <Space direction="vertical" size={0}>
                          <Text strong>{label || row.document_category}</Text>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {row.requirement_code}
                            {row.document_category ? ` · ${row.document_category}` : ''}
                          </Text>
                        </Space>
                      ),
                    },
                    {
                      title: 'Blocks deployment',
                      dataIndex: 'blocks_deployment',
                      render: (v: boolean) =>
                        v ? <Tag color="red">Blocks deployment</Tag> : <Tag>Advisory</Tag>,
                    },
                    {
                      title: 'Class',
                      dataIndex: 'classification_id',
                      render: (id: string | null) => className(id),
                    },
                  ]}
                />
              </>
            ),
          },
          {
            key: 'hire-rates',
            label: 'Internal hire rates',
            children: (
              <>
                <Paragraph type="secondary">
                  Class- or unit-level rates for operational cost attribution (no GL posting).
                </Paragraph>
                <Button
                  type="primary"
                  onClick={() => navigate('/asset/pmv/setup/hire-rates/new')}
                  style={{ marginBottom: 12 }}
                >
                  New hire rate
                </Button>
                <Table<PmvInternalHireRate>
                  rowKey="id"
                  loading={ratesQ.isLoading}
                  dataSource={ratesQ.data ?? []}
                  pagination={false}
                  columns={[
                    {
                      title: 'Rate',
                      dataIndex: 'label',
                      render: (label: string, row) => (
                        <Space direction="vertical" size={0}>
                          <Text strong>
                            {label} — {row.rate_per_hour}/{row.currency}/hour
                          </Text>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {row.rate_code}
                            {row.effective_from
                              ? ` · effective ${String(row.effective_from).slice(0, 10)}`
                              : ''}
                            {row.effective_until
                              ? ` → ${String(row.effective_until).slice(0, 10)}`
                              : ''}
                          </Text>
                        </Space>
                      ),
                    },
                    {
                      title: 'Class',
                      dataIndex: 'classification_id',
                      render: (id: string | null | undefined) =>
                        id ? className(id) : 'Default',
                    },
                    {
                      title: 'Per day',
                      dataIndex: 'rate_per_day',
                      render: (v) => v ?? '—',
                    },
                    {
                      title: 'Active',
                      dataIndex: 'is_active',
                      width: 90,
                      render: (v: boolean) => (v ? <Tag color="green">Yes</Tag> : <Tag>No</Tag>),
                    },
                    {
                      title: 'Actions',
                      width: 180,
                      render: (_: unknown, row) => (
                        <Space size="small">
                          <Button
                            size="small"
                            onClick={() => navigate(`/asset/pmv/setup/hire-rates/${row.id}`)}
                          >
                            Edit
                          </Button>
                          {row.is_active && (
                            <Button
                              size="small"
                              danger
                              loading={deactivateMut.isPending}
                              onClick={() => {
                                Modal.confirm({
                                  title: 'Deactivate hire rate?',
                                  content: `${row.label || row.rate_code} will no longer apply to new cost attribution.`,
                                  okText: 'Deactivate',
                                  okButtonProps: { danger: true },
                                  onOk: () => deactivateMut.mutateAsync(row.id),
                                });
                              }}
                            >
                              Deactivate
                            </Button>
                          )}
                        </Space>
                      ),
                    },
                  ]}
                />
              </>
            ),
          },
        ]}
      />
    </WorkspaceLayout>
  );
};

export default PmvSetupPage;
